import { useEffect, useRef } from 'react';

import { workflowsApi } from '@/api/workflows';

import type { FileState, UploadedEntryFile, UploadedFilesStore } from './uploadedFiles';

/** How often the advisor's browser asks whether its uploads have cleared scanning. */
export const SCAN_POLL_INTERVAL_MS = 15_000;

/**
 * How long one file may sit without a verdict before the advisor is told to upload it again.
 *
 * GuardDuty normally answers in seconds, so reaching this is not a slow scan — it is the
 * `MALWARE_TAG_FAILURE` case, where the scan succeeded but the tag write did not. That object is
 * unreadable forever and leaves no evidence row, so nothing the server can tell us distinguishes it
 * from a scan still in flight. This budget is what ends the wait, and it ends it by blocking,
 * because re-uploading is genuinely the only recovery.
 */
export const SCAN_POLL_BUDGET_MS = 15 * 60_000;

/**
 * Maps the API's status string onto client state.
 *
 * `missing` folds into `blocked` on purpose: it means the object is not there and never got a
 * verdict — no different from the advisor's side than any other refusal, and the remedy is the same
 * re-upload. It is not folded into `scanning`, which would leave the client waiting out its full
 * budget on a file that does not exist.
 *
 * Anything UNRECOGNISED returns undefined and leaves the file alone. An older bundle meeting a
 * newer server must keep waiting rather than guess, and it must never guess towards `clean`.
 */
function toFileState(status: string): FileState | undefined {
  switch (status) {
    case 'ready':
      return 'ready';
    case 'infected':
      return 'infected';
    case 'blocked':
    case 'missing':
      return 'blocked';
    case 'processing':
      return 'processing';
    default:
      return undefined;
  }
}

/** Every file in the store, across named entries and the free-form bucket. */
function allFiles(store: UploadedFilesStore): UploadedEntryFile[] {
  return [...Object.values(store.entries).flat(), ...store.bucket];
}

/** S3 keys of files still awaiting a verdict. */
function pendingKeysOf(store: UploadedFilesStore): string[] {
  return allFiles(store)
    .filter((f) => f.scanState === 'processing' && !!f.s3Key)
    .map((f) => f.s3Key as string);
}

/**
 * Applies a patch to every file matching one of `keys`, across both halves of the store.
 *
 * Matching on `s3Key` rather than `uid` is deliberate: the response is keyed by what the server
 * knows about, and the advisor may have added or removed rows while the request was in flight. A
 * file that has since been removed simply matches nothing, which is the correct outcome.
 */
function patchByKeys(
  store: UploadedFilesStore,
  keys: ReadonlySet<string>,
  patch: (file: UploadedEntryFile) => Partial<UploadedEntryFile>,
): UploadedFilesStore {
  const apply = <T extends UploadedEntryFile>(file: T): T =>
    file.s3Key && keys.has(file.s3Key) ? { ...file, ...patch(file) } : file;

  return {
    entries: Object.fromEntries(
      Object.entries(store.entries).map(([entryId, files]) => [entryId, files.map(apply)]),
    ),
    bucket: store.bucket.map(apply),
  };
}

/**
 * Polls the malware scan status of the advisor's uploaded additional documents until every one has
 * a verdict (ADR-0024, closes R-24).
 *
 * <h3>Why this lives in FormShell rather than in the step</h3>
 * A file uploaded on the additional-documents step must keep resolving while the advisor moves on
 * to later steps — that is the whole point of resolving verdicts during form-filling instead of at
 * submit. Mounting this on the step would tear the loop down the moment they navigated away and
 * restart its budget when they came back.
 *
 * <h3>What it does not do</h3>
 * It does not decide whether a file is safe to use. The malware gate is the bucket policy, which
 * denies reads on any object not tagged `NO_THREATS_FOUND` and never consults this loop. Everything
 * here is advisory: it decides what the advisor is SHOWN and whether Continue is disabled.
 */
export function useAdditionalDocumentScanStatus({
  templateId,
  enabled,
  uploadedFiles,
  onUploadedFilesChange,
}: {
  templateId: string | undefined;
  /**
   * Advisor runtime only. False in originator/approver preview, which never uploads and therefore
   * has nothing to poll — the same guard `useAdditionalDocumentUpload` applies to uploading.
   */
  enabled: boolean;
  uploadedFiles: UploadedFilesStore;
  onUploadedFilesChange: (
    updater: UploadedFilesStore | ((prev: UploadedFilesStore) => UploadedFilesStore),
  ) => void;
}): void {
  // The store is read through a ref so the interval never restarts. Depending on `uploadedFiles`
  // directly would tear down and recreate the timer on every keystroke elsewhere in the form,
  // resetting the 15-second clock each time and, with an unlucky typing cadence, meaning a poll
  // never actually fires.
  const storeRef = useRef(uploadedFiles);
  storeRef.current = uploadedFiles;

  const onChangeRef = useRef(onUploadedFilesChange);
  onChangeRef.current = onUploadedFilesChange;

  /** Keys already asked about at least once — drives the leading-edge poll, see below. */
  const polledRef = useRef<Set<string>>(new Set());

  /** Guards against a slow tick overlapping the next one. */
  const inFlightRef = useRef(false);

  const pollRef = useRef<() => Promise<void>>(async () => {});

  pollRef.current = async () => {
    if (!templateId || !enabled || inFlightRef.current) return;

    const now = Date.now();
    const pending = pendingKeysOf(storeRef.current);
    if (pending.length === 0) return;

    // Expire before asking. A file past its budget stops being polled even if every request from
    // here on fails, so the advisor always reaches a definite answer instead of a spinner that
    // never stops.
    const expired = new Set(
      allFiles(storeRef.current)
        .filter(
          (f) =>
            f.scanState === 'processing' &&
            !!f.s3Key &&
            now - (f.scanPollStartedAt ?? now) > SCAN_POLL_BUDGET_MS,
        )
        .map((f) => f.s3Key as string),
    );
    if (expired.size > 0) {
      onChangeRef.current((prev) => patchByKeys(prev, expired, () => ({ scanState: 'timedOut' })));
    }

    const keys = pending.filter((k) => !expired.has(k));
    if (keys.length === 0) return;

    inFlightRef.current = true;
    try {
      const results = await workflowsApi.getAdditionalDocumentScanStatus(templateId, keys);
      for (const key of keys) polledRef.current.add(key);

      const resolved = new Map<string, { scanState: FileState; scanDetail?: string }>();
      for (const result of results) {
        const scanState = toFileState(result.status);
        // Unrecognised, or still scanning — either way there is nothing to write, and writing
        // `scanning` over `scanning` would churn the store on every tick.
        if (!scanState || scanState === 'processing') continue;
        resolved.set(result.key, { scanState, scanDetail: result.detail ?? undefined });
      }

      if (resolved.size > 0) {
        onChangeRef.current((prev) =>
          patchByKeys(prev, new Set(resolved.keys()), (file) =>
            resolved.get(file.s3Key as string)!,
          ),
        );
      }
    } catch {
      // Transient — a network blip, a 429, or CloudFront's SPA fallback rewriting the response
      // (B17). None of those say anything about whether the file is safe, so the file stays
      // `scanning` and the next tick tries again. Only an explicit server verdict is terminal.
    } finally {
      inFlightRef.current = false;
    }
  };

  // The steady 15-second loop. Deliberately depends on nothing that changes during a session, so
  // it is created once per advisor form and torn down on unmount. It no-ops cheaply on every tick
  // where nothing is pending, which is the overwhelming majority of a session.
  useEffect(() => {
    if (!templateId || !enabled) return;

    let cancelled = false;
    const interval = window.setInterval(() => {
      if (!cancelled) void pollRef.current();
    }, SCAN_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [templateId, enabled]);

  // Leading edge: ask as soon as a file finishes uploading rather than making the advisor watch a
  // spinner for a full interval when GuardDuty usually answers in seconds.
  //
  // Keyed on the pending set, but fires only for keys never asked about before — otherwise every
  // resolution would change the set and trigger another immediate request, turning one settling
  // batch into a burst.
  const pendingKey = pendingKeysOf(uploadedFiles).sort().join('|');
  useEffect(() => {
    if (!templateId || !enabled) return;
    const hasNewKey = pendingKeysOf(storeRef.current).some((k) => !polledRef.current.has(k));
    if (hasNewKey) void pollRef.current();
  }, [templateId, enabled, pendingKey]);
}
